import { ref, update } from "firebase/database";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFirebase } from "../firebase/init.js";
import {
  type PackRef,
  installRulesetPack,
  installScenePack,
  uninstallScenePack,
} from "../lib/pack-install.js";
import PackList, { type PackKind, type PackListEntry, type PackTier } from "./PackList.js";

interface Props {
  open: boolean;
  tableId: string;
  packKind: PackKind;
  guildId?: string | undefined;
  uid: string | undefined;
  installedIds: Set<string>;
  onClose: () => void;
}

export default function PackBrowserModal(props: Props): ReactElement | null {
  const { t } = useTranslation();
  const [tab, setTab] = useState<"public" | "guild" | "user">("public");
  const [busy, setBusy] = useState(false);

  if (!props.open) return null;

  const tier: PackTier =
    tab === "public"
      ? { kind: "public" }
      : tab === "guild" && props.guildId
        ? { kind: "guild", guildId: props.guildId }
        : tab === "user" && props.uid
          ? { kind: "user", uid: props.uid }
          : { kind: "public" };

  async function install(entry: PackListEntry): Promise<void> {
    setBusy(true);
    try {
      const { db } = getFirebase();
      const tablePath = `/tables/${props.tableId}`;
      const packRef: PackRef = {
        id: entry.id,
        version: entry.latestVersion,
        source: entry.sourceTier.kind,
        snapshot: entry.latestVersion,
      };
      const writes =
        props.packKind === "ruleset"
          ? installRulesetPack(tablePath, packRef)
          : installScenePack(tablePath, packRef);
      await update(ref(db), writes);
    } finally {
      setBusy(false);
    }
  }

  async function uninstall(entry: PackListEntry): Promise<void> {
    if (props.packKind !== "scene") return;
    setBusy(true);
    try {
      const { db } = getFirebase();
      await update(ref(db), uninstallScenePack(`/tables/${props.tableId}`, entry.id));
    } finally {
      setBusy(false);
    }
  }

  return (
    // biome-ignore lint/a11y/useSemanticElements: native <dialog> requires imperative showModal(); ARIA dialog pattern is intentional
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="pack-browser-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
        <h2 id="pack-browser-title" className="text-lg font-semibold">
          {props.packKind === "ruleset" ? t("packs.title.ruleset") : t("packs.title.scene")}
        </h2>

        <div role="tablist" className="mt-3 flex gap-2 border-b">
          {(["public", "guild", "user"] as const).map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className={`px-3 py-1 text-sm ${tab === key ? "border-b-2 border-emerald-600 font-medium" : ""}`}
              onClick={() => setTab(key)}
              disabled={(key === "guild" && !props.guildId) || (key === "user" && !props.uid)}
            >
              {t(`packs.tab.${key}`)}
            </button>
          ))}
        </div>

        <div className="mt-3">
          <PackList
            tier={tier}
            packKind={props.packKind}
            onInstall={install}
            {...(props.packKind === "scene" ? { onUninstall: uninstall } : {})}
            installedIds={props.installedIds}
            disabled={busy}
          />
        </div>

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            className="rounded border px-3 py-1 text-sm"
            onClick={props.onClose}
          >
            {t("edit.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}
